import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/Products.css';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { loading, error, getData } = useBusinessData();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [isLoadingProduct, setIsLoadingProduct] = useState(true);

  // Get products configuration
  const productsConfig = getData('products', {});
  const currency = productsConfig.currency || '';

  useEffect(() => {
    if (!loading && productsConfig.items) {
      const foundProduct = productsConfig.items.find(p => p.id === productId);
      if (foundProduct) {
        setProduct(foundProduct);
        // Set first image as active
        if (foundProduct.images && foundProduct.images.length > 0) {
          setActiveImage(foundProduct.images[0]);
        }
      }
      setIsLoadingProduct(false);
    }
  }, [loading, productId, productsConfig.items]);

  if (loading || isLoadingProduct) return <div className="loading">Loading product details...</div>;
  if (error) return <div className="error">Error loading data: {error}</div>;

  if (!product) {
    return (
      <div className="container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <h2>Product not found</h2>
        <p>The product you are looking for does not exist or has been removed.</p>
        <Link to="/products" className="btn btn-primary" style={{ marginTop: '20px', display: 'inline-block' }}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container product-detail-container">
        <Link to="/products" className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Products
        </Link>

        <div className="product-detail-layout">
          {/* Left Column: Image Gallery */}
          <div className="product-gallery">
            <div className="main-image-container">
              <img 
                src={activeImage || 'https://via.placeholder.com/600x400?text=No+Image'} 
                alt={product.name} 
                className="main-image" 
              />
            </div>
            
            {product.images && product.images.length > 1 && (
              <div className="thumbnail-strip">
                {product.images.map((img, index) => (
                  <div 
                    key={index} 
                    className={`thumbnail ${activeImage === img ? 'active' : ''}`}
                    onClick={() => setActiveImage(img)}
                  >
                    <img src={img} alt={`${product.name} view ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Product Info */}
          <div className="product-content">
            <h1>{product.name}</h1>
            
            <div className="detail-meta">
              <span className="detail-category">{product.category}</span>
              <span className="detail-price">
                {product.price && !isNaN(product.price) 
                  ? `${currency} ${product.price}`
                  : product.price}
              </span>
            </div>

            <div className="detail-description">
              <p>{product.fullDescription || product.description || product.shortDescription}</p>
            </div>

            {/* Features List */}
            {product.features && product.features.length > 0 && (
              <div className="product-features-section">
                <h3 className="section-title-small">Key Features</h3>
                <ul className="features-list">
                  {product.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specifications Table */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="product-specs-section">
                <h3 className="section-title-small">Specifications</h3>
                <table className="specs-table">
                  <tbody>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <tr key={key}>
                        <td className="spec-label">{key}</td>
                        <td className="spec-value">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CTA Section */}
            <div className="cta-container">
              <h3>Interested in this product?</h3>
              <p className="cta-text">Contact us for bulk orders, customization, or more information.</p>
              <Link to={product.ctaLink || "/contact"} className="btn btn-primary">
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px' }} />
                {product.ctaText || "Enquire Now"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
